import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { BookingPageSettingsResponse } from "./contracts/BookingPageSettingsResponse";
import { TrialRequestResponse } from "./contracts/TrialRequestResponse";
import {
  confirmTrialRequest,
  declineTrialRequest,
  deleteTrialRequest,
  fetchBookingPageSettings,
  fetchInboxCount,
  fetchPendingTrialRequestCount,
  fetchTrialRequests,
  removeBookingPageImage,
  setHeroPreset,
  updateBookingPageSettings,
  uploadBookingPageImage,
} from "./slice-extensions/trial-requests-slice-extension";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface TrialRequestsState {
  items: TrialRequestResponse[];
  totalCount: number;
  page: number;
  totalPages: number;
  listStatus: RequestStatus;
  /** Only the newest in-flight list fetch may write results (stale-response guard). */
  latestListRequestId: string | null;
  pendingCount: number;
  /** Total items waiting in the Inbox (nav badge). */
  inboxCount: number;
  bookingPageSettings: BookingPageSettingsResponse | null;
  bookingPageSettingsStatus: RequestStatus;
  bookingPageSettingsError: string | null;
}

const initialState: TrialRequestsState = {
  items: [],
  totalCount: 0,
  page: 1,
  totalPages: 0,
  listStatus: "idle",
  latestListRequestId: null,
  pendingCount: 0,
  inboxCount: 0,
  bookingPageSettings: null,
  bookingPageSettingsStatus: "idle",
  bookingPageSettingsError: null,
};

const replaceItem = (items: TrialRequestResponse[], updated: TrialRequestResponse) =>
  items.map((item) => (item.id === updated.id ? updated : item));

const trialRequestsSlice = createSlice({
  name: "trialRequests",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrialRequests.pending, (state, action) => {
        state.listStatus = "loading";
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(fetchTrialRequests.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) {
          return; // a newer fetch superseded this one — drop the stale result
        }
        state.listStatus = "succeeded";
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchTrialRequests.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) {
          return;
        }
        state.listStatus = "failed";
      })
      .addCase(fetchInboxCount.fulfilled, (state, action) => {
        state.inboxCount = action.payload;
      })
      .addCase(fetchPendingTrialRequestCount.fulfilled, (state, action) => {
        state.pendingCount = action.payload;
      })
      .addCase(confirmTrialRequest.fulfilled, (state, action) => {
        state.items = replaceItem(state.items, action.payload);
        state.pendingCount = Math.max(0, state.pendingCount - 1);
      })
      .addCase(declineTrialRequest.fulfilled, (state, action) => {
        state.items = replaceItem(state.items, action.payload);
        state.pendingCount = Math.max(0, state.pendingCount - 1);
      })
      .addCase(deleteTrialRequest.fulfilled, (state, action) => {
        const removed = state.items.find((item) => item.id === action.payload);
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        if (removed?.status === "Pending") {
          state.pendingCount = Math.max(0, state.pendingCount - 1);
        }
      })
      .addCase(fetchBookingPageSettings.fulfilled, (state, action) => {
        state.bookingPageSettings = action.payload;
        state.bookingPageSettingsStatus = "succeeded";
        state.bookingPageSettingsError = null;
      })
      .addCase(updateBookingPageSettings.pending, (state) => {
        state.bookingPageSettingsStatus = "loading";
        state.bookingPageSettingsError = null;
      })
      .addCase(updateBookingPageSettings.fulfilled, (state, action) => {
        state.bookingPageSettings = action.payload;
        state.bookingPageSettingsStatus = "succeeded";
      })
      .addCase(updateBookingPageSettings.rejected, (state, action) => {
        state.bookingPageSettingsStatus = "failed";
        state.bookingPageSettingsError = action.payload?.message ?? "Update failed";
      })
      .addCase(uploadBookingPageImage.pending, (state) => {
        state.bookingPageSettingsStatus = "loading";
        state.bookingPageSettingsError = null;
      })
      .addCase(uploadBookingPageImage.fulfilled, (state, action) => {
        state.bookingPageSettings = action.payload;
        state.bookingPageSettingsStatus = "succeeded";
      })
      .addCase(uploadBookingPageImage.rejected, (state, action) => {
        state.bookingPageSettingsStatus = "failed";
        state.bookingPageSettingsError = action.payload?.message ?? "Upload failed";
      })
      .addCase(removeBookingPageImage.pending, (state) => {
        state.bookingPageSettingsStatus = "loading";
        state.bookingPageSettingsError = null;
      })
      .addCase(removeBookingPageImage.fulfilled, (state, action) => {
        state.bookingPageSettings = action.payload;
        state.bookingPageSettingsStatus = "succeeded";
      })
      .addCase(removeBookingPageImage.rejected, (state, action) => {
        state.bookingPageSettingsStatus = "failed";
        state.bookingPageSettingsError = action.payload?.message ?? "Remove failed";
      })
      .addCase(setHeroPreset.pending, (state) => {
        state.bookingPageSettingsStatus = "loading";
        state.bookingPageSettingsError = null;
      })
      .addCase(setHeroPreset.fulfilled, (state, action) => {
        state.bookingPageSettings = action.payload;
        state.bookingPageSettingsStatus = "succeeded";
      })
      .addCase(setHeroPreset.rejected, (state, action) => {
        state.bookingPageSettingsStatus = "failed";
        state.bookingPageSettingsError = action.payload?.message ?? "Update failed";
      });
  },
});

export const selectTrialRequests = (state: RootState) => state.trialRequests.items;
export const selectTrialRequestsListStatus = (state: RootState) => state.trialRequests.listStatus;
export const selectTrialRequestsPage = (state: RootState) => ({
  page: state.trialRequests.page,
  totalPages: state.trialRequests.totalPages,
  totalCount: state.trialRequests.totalCount,
});
export const selectPendingTrialRequestCount = (state: RootState) => state.trialRequests.pendingCount;
export const selectInboxCount = (state: RootState) => state.trialRequests.inboxCount;
export const selectBookingPageSettings = (state: RootState) => state.trialRequests.bookingPageSettings;
export const selectBookingPageSettingsStatus = (state: RootState) =>
  state.trialRequests.bookingPageSettingsStatus;
export const selectBookingPageSettingsError = (state: RootState) =>
  state.trialRequests.bookingPageSettingsError;

export default trialRequestsSlice.reducer;
